import angular, {
  ICompileService,
  IQService,
  IRootScopeService,
  ITemplateCacheService,
  ITimeoutService
} from 'angular';
import { Percentage } from '../interfaces/AdvanceTypes';

export class PPDFService {
  public static ID = 'PPDFService';
  public static $inject = [
    '$templateCache',
    '$compile',
    '$rootScope',
    '$timeout',
    '$q',
    'lodashService'
  ];

  constructor(
    private $templateCache: ITemplateCacheService,
    private $compile: ICompileService,
    private $rootScope: IRootScopeService,
    private $timeout: ITimeoutService,
    private $q: IQService,
    private lodashService: Lodash
  ) {}

  generatePpdf(templateName: string, data: unknown): angular.IPromise<void> {
    const { scope, compiledElem } = this.compileTemplate(templateName, data);

    return this.$timeout(() => {
      return this.waitForReactComponentsToRender()
        .then(() => {
          const newWindow = window.open('', '_blank');
          this.replaceDocumentContent(newWindow.document, compiledElem.html());
        })
        .finally(() => {
          scope.$destroy();
        });
    }, 0);
  }

  generatePpdfInCurrentWindow(
    templateName: string,
    data: unknown
  ): angular.IPromise<void> {
    const { scope, compiledElem } = this.compileTemplate(templateName, data);

    return this.$timeout(() => {
      return this.waitForReactComponentsToRender()
        .then(() => {
          this.replaceDocumentContent(document, compiledElem.html());
          return this.waitForPaintAndStartConversion();
        })
        .finally(() => {
          scope.$destroy();
        });
    }, 0);
  }

  private compileTemplate(
    templateName: string,
    data: unknown
  ): { scope: angular.IScope; compiledElem: JQLite } {
    this.generateStepStatus(data);
    const template = this.$templateCache.get(templateName);
    if (!template) {
      throw new TemplateNotFoundError(
        `Template(${templateName}) could not be found`
      );
    }
    const html = `<div>${template}</div>`;
    const scope = angular.extend(this.$rootScope.$new(), data);
    scope.jsonparse = JSON.parse;
    const compiledElem = this.$compile(html)(scope);
    return { scope, compiledElem };
  }

  private replaceDocumentContent(
    targetDocument: Document,
    htmlContent: string
  ): void {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    targetDocument.replaceChild(
      targetDocument.importNode(doc.documentElement, true),
      targetDocument.documentElement
    );
  }

  private waitForReactComponentsToRender(): angular.IPromise<void> {
    return this.$q((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.$timeout(() => resolve(), 0);
        });
      });
    });
  }

  private waitForPaintAndStartConversion(): angular.IPromise<void> {
    return this.$q((resolve) => {
      requestAnimationFrame(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (typeof hiqPdfConverter_startConversion === 'function') {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          hiqPdfConverter_startConversion();
        }
        resolve();
      });
    });
  }

  private generateStepStatus(data: any): void {
    if (!data?.completedSteps) return;

    this.lodashService.forEach(data.completedSteps, (step) => {
      switch (step.status) {
        case 0:
          step.stepStatus = 'ToDo';
          break;
        case 1:
          step.stepStatus = 'Hold';
          break;
        case 2:
          step.stepStatus = 'Completed';
          break;
        case 3:
          step.stepStatus = 'Abandoned';
          break;
        default:
          step.stepStatus = '';
          break;
      }
    });
  }
}

export class TemplateNotFoundError extends Error {
  constructor(message) {
    super(message);
  }
}

export class FlowHTMLPayload {
  public flowModelName: string;
  public flowIdentifier: string;
  public subject: string;
  public progress: Percentage;
  public inProgressSteps: FlowStepHTMLPayload[];
  public completedSteps: FlowStepHTMLPayload[];
}

export class FlowStepHTMLPayload {
  public dateCompleted: Date;
  public name: string;
}

angular
  .module('flowingly.runner.services')
  .service(PPDFService.ID, PPDFService);

export type PPDFServiceType = InstanceType<typeof PPDFService>;
